"use client"

import { Check, X } from "lucide-react"
import { useCallback, useEffect, useState } from "react"

import { Logo } from "@/components/Logo"
import { cn } from "@/lib/utils"
import {
  getWaitlistCount,
  WaitlistCountUnavailableError,
} from "@/services/waitlistService"

import {
  ENTERPRISE_PLAN,
  SHOW_BETA_QUOTES,
  FAQS,
  FEATURES,
  NEW_WAY,
  OLD_WAY,
  PLANS,
  QUOTES,
  STAGES,
} from "./content"
import { FeatureIcon } from "./FeatureIcon"
import { HeroSignup } from "./HeroSignup"
import { WorkbenchIllustration } from "./WorkbenchIllustration"
import { LandingFaq } from "./LandingFaq"

export function MarketingLanding() {
  const [authorCount, setAuthorCount] = useState<number | null>(null)
  const [authorCountStatus, setAuthorCountStatus] = useState<"loading" | "ready" | "error">("loading")

  const refreshCount = useCallback(() => {
    setAuthorCountStatus("loading")
    getWaitlistCount()
      .then((count) => {
        setAuthorCount(count)
        setAuthorCountStatus("ready")
      })
      .catch((err: unknown) => {
        // Missing SaaS origin is expected on a stranger's local preview.
        // Real BFF/SaaS failures still log; never invent a count in the hero.
        if (!(err instanceof WaitlistCountUnavailableError)) {
          console.error("Waitlist author count failed:", err)
        }
        setAuthorCount(null)
        setAuthorCountStatus("error")
      })
  }, [])

  useEffect(() => {
    refreshCount()
  }, [refreshCount])

  return (
    <div className="bg-[var(--paper,#fafaf9)] text-stone-800">
      {/* Hero — noir card */}
      <section id="top" className="mx-auto mt-3 max-w-[1200px] px-4 sm:px-8" aria-label="Hero">
        <div
          className="overflow-hidden rounded-[28px] bg-cover bg-right bg-no-repeat px-6 py-16 shadow-[0_12px_28px_-8px_rgb(28_25_23/0.12),0_4px_8px_-4px_rgb(28_25_23/0.06)] sm:px-[72px] sm:py-24"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(20,17,16,0.94) 0%, rgba(20,17,16,0.78) 45%, rgba(20,17,16,0.35) 100%), url('/hero-silhouette.jpg')",
          }}
        >
          <HeroSignup
            authorCount={authorCount}
            authorCountStatus={authorCountStatus}
            onSignupSuccess={refreshCount}
          />
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="mx-auto max-w-[1200px] px-4 pt-24 sm:px-8" aria-labelledby="how-heading">
        <div className="mb-12 flex flex-col items-center gap-3.5 text-center">
          <span className="font-sans text-xs font-semibold tracking-[0.16em] text-rose-700 uppercase">
            How it works
          </span>
          <h2
            id="how-heading"
            className="font-display m-0 text-[clamp(2rem,4vw,3rem)] leading-[1.15] font-medium tracking-[-0.02em] text-balance"
          >
            Five stages, one manuscript.
          </h2>
          <p className="m-0 max-w-[56ch] text-[17px] text-pretty text-stone-600">
            Each stage builds on the last, so the guidance your draft gets in Content still remembers the
            choices you made in Foundation.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {STAGES.map((stage) => (
            <div
              key={stage.num}
              className="flex flex-col gap-2.5 rounded-[20px] border border-stone-200 bg-white p-7 shadow-[0_1px_2px_0_rgb(28_25_23/0.04)]"
            >
              <span className="font-display text-[34px] leading-none font-medium text-rose-500">{stage.num}</span>
              <h3 className="m-0 text-lg font-semibold tracking-[-0.02em]">{stage.name}</h3>
              <p className="m-0 text-sm leading-[1.55] text-pretty text-stone-600">{stage.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Product preview — lo-fi workbench illustration (deliberately not
          a screenshot; see WorkbenchIllustration) */}
      <section
        className="mx-auto max-w-[1200px] px-4 pt-24 text-center sm:px-8"
        aria-labelledby="workbench-heading"
      >
        <span className="font-sans text-xs font-semibold tracking-[0.16em] text-rose-700 uppercase">
          The workbench
        </span>
        <h2
          id="workbench-heading"
          className="font-display mt-3.5 mb-10 text-[clamp(2rem,4vw,3rem)] leading-[1.15] font-medium tracking-[-0.02em] text-balance"
        >
          Every book, every stage, in one place.
        </h2>
        <div
          className="mx-auto max-w-[1040px] rounded-[28px] border border-stone-200 bg-white p-3 shadow-[0_12px_28px_-8px_rgb(28_25_23/0.12),0_4px_8px_-4px_rgb(28_25_23/0.06)]"
          role="img"
          aria-label="Stylized illustration of the Authorythm workbench: pen names, project cards with five-stage progress, and a draft in progress"
        >
          <WorkbenchIllustration />
        </div>
        <p className="mx-auto mt-5 text-sm text-stone-500">
          Pen names, projects, and drafts — organized the way a backlist actually grows.
        </p>
      </section>

      {/* Features */}
      <section
        id="features"
        className="mx-auto max-w-[1200px] px-4 pt-24 sm:px-8"
        aria-labelledby="features-heading"
      >
        <div className="mb-12 flex max-w-[640px] flex-col gap-3.5">
          <span className="font-sans text-xs font-semibold tracking-[0.16em] text-rose-700 uppercase">
            Features
          </span>
          <h2
            id="features-heading"
            className="font-display m-0 text-[clamp(2rem,4vw,3rem)] leading-[1.15] font-medium tracking-[-0.02em] text-balance"
          >
            The whole pipeline, not another blank page.
          </h2>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((feature) => (
            <div
              key={feature.name}
              className="flex flex-col gap-3 rounded-[20px] border border-stone-200 bg-white p-6"
            >
              <span className="grid size-10 place-items-center rounded-[10px] bg-rose-50 text-rose-600">
                <FeatureIcon name={feature.icon} className="size-5" />
              </span>
              <h3 className="m-0 text-lg font-semibold tracking-[-0.02em]">{feature.name}</h3>
              <p className="m-0 text-sm leading-[1.55] text-pretty text-stone-600">{feature.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The math */}
      <section className="mx-auto max-w-[1200px] px-4 pt-24 sm:px-8" aria-labelledby="math-heading">
        <div className="mb-12 flex flex-col items-center gap-3.5 text-center">
          <span className="font-sans text-xs font-semibold tracking-[0.16em] text-rose-700 uppercase">
            The math
          </span>
          <h2
            id="math-heading"
            className="font-display m-0 text-[clamp(2rem,4vw,3rem)] leading-[1.15] font-medium tracking-[-0.02em] text-balance"
          >
            A first book shouldn&apos;t cost $2,500.
          </h2>
          <p className="m-0 max-w-[56ch] text-[17px] text-pretty text-stone-600">
            Assembled à la carte, editing and tooling for one manuscript runs into real money — and half a
            year. Authorythm folds the pipeline into a monthly plan with included AI usage.
          </p>
        </div>

        <div className="mx-auto grid max-w-[920px] gap-5 md:grid-cols-2">
          <div className="rounded-[20px] border border-stone-200 bg-stone-100 p-7">
            <h3 className="mb-[18px] text-lg font-semibold tracking-[-0.02em]">Piece by piece</h3>
            <div className="flex flex-col">
              {OLD_WAY.map((row) => (
                <div
                  key={row.item}
                  className="flex items-center gap-2.5 border-b border-stone-200 py-[11px] text-sm last:border-b-0"
                >
                  <X className="size-[15px] shrink-0 text-stone-400" strokeWidth={1.75} aria-hidden />
                  <span className="text-stone-600">{row.item}</span>
                  <span className="ml-auto font-mono text-[13px] text-stone-600">{row.cost}</span>
                </div>
              ))}
            </div>
            <div className="flex items-baseline justify-between pt-[18px]">
              <span className="font-sans text-xs font-semibold tracking-[0.16em] text-stone-600 uppercase">
                Six months
              </span>
              <span className="font-display text-[26px] font-medium text-stone-800">about $2,500</span>
            </div>
          </div>

          <div className="rounded-[20px] border border-rose-200 bg-rose-50 p-7">
            <h3 className="mb-[18px] text-lg font-semibold tracking-[-0.02em]">With Authorythm</h3>
            <div className="flex flex-col">
              {NEW_WAY.map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-2.5 border-b border-rose-100 py-[11px] text-sm last:border-b-0"
                >
                  <Check className="size-[15px] shrink-0 text-rose-600" strokeWidth={1.75} aria-hidden />
                  <span className="text-stone-800">{item}</span>
                  <span className="ml-auto text-[13px] font-medium text-rose-700">Included</span>
                </div>
              ))}
            </div>
            <div className="flex items-baseline justify-between pt-[18px]">
              <span className="font-sans text-xs font-semibold tracking-[0.16em] text-rose-700 uppercase">
                Days, not months
              </span>
              <span className="font-display text-[26px] font-medium text-rose-700">one plan</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials — gated until real founding-author quotes exist
          (SHOW_BETA_QUOTES in content.ts) */}
      {SHOW_BETA_QUOTES ? (
        <section
          className="mx-auto max-w-[1200px] px-4 pt-24 sm:px-8"
          aria-labelledby="testimonials-heading"
        >
          <div className="mx-auto mb-12 flex max-w-[520px] items-center gap-3">
            <div
              className="h-px flex-1 bg-gradient-to-r from-transparent via-rose-200 to-transparent"
              aria-hidden
            />
            <span
              id="testimonials-heading"
              className="font-sans text-xs font-semibold tracking-[0.16em] whitespace-nowrap text-rose-600 uppercase"
            >
              From the beta
            </span>
            <div
              className="h-px flex-1 bg-gradient-to-r from-transparent via-rose-200 to-transparent"
              aria-hidden
            />
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {QUOTES.map((quote) => (
              <figure key={quote.name} className="m-0 flex flex-col gap-[18px]">
                <blockquote className="font-display m-0 text-[19px] leading-[1.55] font-normal text-pretty text-stone-600 italic">
                  &ldquo;{quote.text}&rdquo;
                </blockquote>
                <figcaption className="flex items-center gap-3">
                  <span className="grid size-[38px] place-items-center rounded-full bg-rose-100 text-sm font-semibold text-rose-700">
                    {quote.initial}
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-semibold">{quote.name}</span>
                    <span className="text-xs text-stone-500">{quote.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      ) : null}

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-[1200px] px-4 pt-24 sm:px-8" aria-labelledby="pricing-heading">
        <div className="mb-12 flex flex-col items-center gap-3.5 text-center">
          <span className="font-sans text-xs font-semibold tracking-[0.16em] text-rose-700 uppercase">
            Pricing
          </span>
          <h2
            id="pricing-heading"
            className="font-display m-0 text-[clamp(2rem,4vw,3rem)] leading-[1.15] font-medium tracking-[-0.02em] text-balance"
          >
            Plans for every backlist pace.
          </h2>
          <p className="m-0 max-w-[56ch] text-[17px] text-pretty text-stone-600">
            Monthly plans with included AI usage for every stage of the pipeline.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={cn(
                "flex flex-col gap-5 rounded-[20px] border p-6",
                plan.highlighted
                  ? "border-rose-200 bg-rose-50 shadow-[0_8px_24px_-8px_rgb(225_29_72/0.2)]"
                  : "border-stone-200 bg-white shadow-[0_1px_2px_0_rgb(28_25_23/0.04)]"
              )}
            >
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <h3 className="m-0 text-lg font-semibold tracking-[-0.02em]">{plan.name}</h3>
                  {plan.badge ? (
                    <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                      {plan.badge}
                    </span>
                  ) : null}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-[28px] leading-none font-medium text-stone-500">
                    {plan.price}
                  </span>
                  {plan.period ? (
                    <span className="text-sm text-stone-500">{plan.period}</span>
                  ) : null}
                </div>
                <p className="m-0 text-sm text-stone-600">{plan.blurb}</p>
              </div>

              <div className="flex flex-1 flex-col gap-2.5">
                {plan.features.map((item) => (
                  <div key={item} className="flex items-start gap-2.5 text-sm text-stone-600">
                    <Check className="mt-0.5 size-[15px] shrink-0 text-rose-600" strokeWidth={1.75} aria-hidden />
                    {item}
                  </div>
                ))}
              </div>

              <a
                href="#signup"
                className={cn(
                  "flex justify-center rounded-[14px] px-[18px] py-3 text-[14px] font-semibold transition-colors",
                  plan.highlighted
                    ? "bg-rose-600 text-white hover:bg-rose-700 hover:shadow-[0_8px_24px_-8px_rgb(225_29_72/0.25)]"
                    : "border border-stone-300 bg-white text-stone-800 hover:border-stone-400"
                )}
              >
                Join the waitlist
              </a>
            </div>
          ))}
        </div>

        {/* Enterprise — sales-led, not a self-serve price card */}
        <div className="mt-5 rounded-[20px] border border-stone-800 bg-stone-900 p-6 text-stone-50 shadow-[0_12px_28px_-8px_rgb(28_25_23/0.2)] sm:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-10">
            <div className="flex max-w-[40ch] flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="m-0 text-lg font-semibold tracking-[-0.02em]">
                  {ENTERPRISE_PLAN.name}
                </h3>
                <span className="rounded-full border border-stone-600 px-2 py-0.5 text-[11px] font-semibold text-stone-300">
                  For publishing houses
                </span>
              </div>
              <span className="font-display text-[28px] leading-none font-medium text-stone-300">
                {ENTERPRISE_PLAN.price}
              </span>
              <p className="m-0 text-sm leading-relaxed text-stone-400">{ENTERPRISE_PLAN.blurb}</p>
              <a
                href={ENTERPRISE_PLAN.ctaHref}
                className="mt-2 inline-flex w-fit justify-center rounded-[14px] bg-rose-600 px-[22px] py-3 text-[14px] font-semibold text-white transition-colors hover:bg-rose-700 hover:shadow-[0_8px_24px_-8px_rgb(225_29_72/0.25)]"
              >
                {ENTERPRISE_PLAN.ctaLabel}
              </a>
            </div>
            <ul className="m-0 grid list-none gap-2.5 p-0 sm:grid-cols-2 lg:max-w-[560px]">
              {ENTERPRISE_PLAN.features.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-stone-300">
                  <Check className="mt-0.5 size-[15px] shrink-0 text-rose-400" strokeWidth={1.75} aria-hidden />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-[760px] px-4 pt-24 sm:px-8" aria-labelledby="faq-heading">
        <div className="mb-10 flex flex-col items-center gap-3.5 text-center">
          <span className="font-sans text-xs font-semibold tracking-[0.16em] text-rose-700 uppercase">
            Questions
          </span>
          <h2
            id="faq-heading"
            className="font-display m-0 text-[clamp(2rem,4vw,3rem)] leading-[1.15] font-medium tracking-[-0.02em] text-balance"
          >
            Asked, answered.
          </h2>
        </div>

        <LandingFaq items={[...FAQS]} />
      </section>

      {/* Closing CTA */}
      <section className="mx-auto max-w-[1200px] px-4 pt-24 sm:px-8" aria-labelledby="closing-heading">
        <div className="rounded-[28px] bg-stone-900 px-6 py-16 text-center shadow-[0_12px_28px_-8px_rgb(28_25_23/0.12),0_4px_8px_-4px_rgb(28_25_23/0.06)] sm:px-12 sm:py-[72px]">
          <Logo variant="white" size="sm" className="mx-auto mb-[22px]" />
          <h2
            id="closing-heading"
            className="font-display m-0 text-[clamp(2.5rem,5vw,4rem)] leading-[1.15] font-medium tracking-[-0.02em] text-balance text-stone-50"
          >
            Finish the book.
          </h2>
          <p className="mx-auto mt-4 max-w-[44ch] text-[17px] text-stone-400">
            From premise to manuscript, in five stages. Yours to refine, yours to keep.
          </p>
          <a
            href="#signup"
            className="mt-7 inline-flex rounded-[14px] bg-rose-600 px-[26px] py-[13px] text-[15px] font-semibold text-white transition-colors hover:bg-rose-700 hover:shadow-[0_8px_24px_-8px_rgb(225_29_72/0.25)]"
          >
            Join the waitlist
          </a>
        </div>
      </section>
    </div>
  )
}
